import { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '../lib/api.js';

/**
 * Minimal data-fetching hook. Deliberately not a cache library — the pages here
 * are mostly one-shot reads, and dashboard numbers should be fresh on load
 * rather than served from a stale cache.
 */
export function useFetch(path, { skip = false } = {}) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(!skip);
  const controllerRef = useRef(null);

  const load = useCallback(async () => {
    if (skip || !path) {
      setLoading(false);
      return;
    }
    controllerRef.current?.abort();
    const controller = new AbortController();
    controllerRef.current = controller;

    setLoading(true);
    setError(null);
    try {
      const result = await api.get(path, { signal: controller.signal });
      if (!controller.signal.aborted) setData(result);
    } catch (err) {
      if (err.name !== 'AbortError' && !controller.signal.aborted) setError(err);
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  }, [path, skip]);

  useEffect(() => {
    load();
    return () => controllerRef.current?.abort();
  }, [load]);

  return { data, error, loading, reload: load };
}
